import { useCallback, useEffect, useRef, useState } from "react";
import { getMaintenance, isAuthenticationError } from "./api";
import { formatCount } from "./numberFormat";

type MaintenanceEvent = {
  id: string; at: string; action: string; files: number | null; bytes: number | null;
  candidate_files: number | null; candidate_bytes: number | null; seconds: number | null;
  reason: string; excluded: { reason: string; cycles: number }[];
};
export type MaintenanceReport = {
  environment: string; generated_at: string; state: string;
  schedule: { enabled: boolean | null; next_check: string | null; eligible_after: string | null;
    minimum_age_hours: number; minimum_interval_hours: number };
  last_attempt: MaintenanceEvent | null; last_cleanup: MaintenanceEvent | null;
  last_nonempty_cleanup: MaintenanceEvent | null; history: MaintenanceEvent[];
  volumes: { id: string; locations: string[]; total_bytes: number; available_bytes: number }[];
  unavailable_locations: string[];
};
const date = (value?: string | null) => value ? new Date(value).toLocaleString("es-ES", { timeZone: "Atlantic/Canary" }) : "Sin registro";
const gib = (value?: number | null) => value == null ? "—" : `${(value / 2 ** 30).toLocaleString("es-ES", { maximumFractionDigits: 2 })} GiB`;
const count = (value?: number | null) => value == null ? "—" : formatCount(value);
const labels: Record<string, string> = {
  succeeded: "Completado", running: "En curso", deferred: "Aplazado con seguridad", failed: "Fallido",
  interrupted: "Interrumpido; revisar Logs", unknown: "Estado no disponible", never: "Aún no ejecutado",
  CATALOG_CYCLE_ACTIVE: "Hay una actualización activa o pausada. Se comprobará de nuevo en la siguiente ventana.",
  IMAGE_WORKERS_BUSY_OR_UNKNOWN: "Los ejecutores están ocupados o no se ha podido comprobar su estado.",
  maintenance_or_worker_lock: "Otro proceso mantiene el bloqueo de seguridad.",
  "lifecycle-protected": "Ciclo reciente, activo, último, configurado o fuera de la política de fallidos",
  "invalid-or-recent-evidence": "Evidencia incompleta, inválida o archivos demasiado recientes",
};
const eventLabel = (action: string) => ({
  "catalog.image_spool.automatic_started": "Análisis automático iniciado",
  "catalog.image_spool.automatic_succeeded": "Limpieza automática completada",
  "catalog.image_spool.automatic_deferred": "Limpieza aplazada",
  "catalog.image_spool.automatic_failed": "Limpieza automática fallida",
  "catalog.image_storage.maintained": "Revisión de almacenamiento y retención anterior",
  "catalog.image_storage.failed": "Incidencia de mantenimiento",
}[action] ?? "Mantenimiento registrado");

export function Maintenance({ onSessionExpired }: { onSessionExpired: () => void }) {
  const [data, setData] = useState<MaintenanceReport | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const pending = useRef<AbortController | null>(null);
  const refresh = useCallback(async () => {
    if (pending.current) return;
    const controller = new AbortController();
    pending.current = controller;
    const timeout = window.setTimeout(() => controller.abort(), 15000);
    setLoading(true);
    try {
      const result = await getMaintenance(controller.signal);
      if (!controller.signal.aborted) { setData(result); setError(""); }
    } catch (failure) {
      if (isAuthenticationError(failure)) onSessionExpired();
      setError("No se pudo actualizar el mantenimiento. Los datos anteriores, si existen, no representan el estado actual.");
    } finally {
      window.clearTimeout(timeout); pending.current = null; setLoading(false);
    }
  }, [onSessionExpired]);
  useEffect(() => {
    void refresh();
    const timer = window.setInterval(() => void refresh(), 30000);
    return () => { window.clearInterval(timer); pending.current?.abort(); };
  }, [refresh]);
  const last = data?.last_cleanup;
  return <section id="mantenimiento" className="page-section route-panel maintenance-panel">
    <div className="panel-heading"><div><p className="eyebrow">Almacenamiento · {data?.environment === "production" ? "PROD" : "TEST"}</p><h2>Limpieza de temporales y espacio</h2></div>
      <button className="secondary-button" disabled={loading} onClick={() => void refresh()}>{loading ? "Consultando…" : "Actualizar estado"}</button></div>
    <p className="maintenance-intro">{data?.environment === "production" ? "PROD: limpieza automática desactivada. El ciclo supervisado retira sólo sus copias temporales de imágenes después de verificar cada lote; conserva imágenes publicadas y evidencias." : "Retirada automática de temporales de imágenes de ciclos fallidos o cancelados de más de 72 horas. Las fotos publicadas, la caché y las evidencias quedan fuera de esta limpieza."}</p>
    {error && <p role="alert" className="form-error">{error}</p>}
    {!data && !error && <p role="status">Consultando el estado real del mantenimiento…</p>}
    {data && <>
      <div className="maintenance-grid">
        <article className="maintenance-card"><h3>Automatización</h3>
          <p className="maintenance-status"><span className={`environment-dot${data.schedule.enabled === null ? " dot-unknown" : data.schedule.enabled ? "" : " dot-error"}`} aria-hidden="true" /><strong>{data.schedule.enabled === null ? "Programador no comprobado" : data.schedule.enabled ? "Programación activa" : "Programación inactiva"}</strong></p>
          <p>{data.environment === "production" ? "Sin programador de limpieza; retirada verificada dentro de cada lote aprobado." : `Comprobación cada hora; como máximo una limpieza cada ${data.schedule.minimum_interval_hours} h.`}</p>
          <dl className="maintenance-kv">
            <div><dt>Próxima comprobación</dt><dd>{data.schedule.next_check || "No disponible"}</dd></div>
            <div><dt>Próxima limpieza permitida desde</dt><dd>{data.schedule.eligible_after ? date(data.schedule.eligible_after) : "La primera ventana segura"}</dd></div>
          </dl>
        </article>
        <article className="maintenance-card"><h3>Último intento automático</h3>
          <span className={`soft-chip ${error ? "chip-warn" : { succeeded: "chip-ok", deferred: "chip-warn", failed: "chip-err", interrupted: "chip-err" }[data.state] ?? ""}`}><strong>{error ? "Datos sin actualizar" : labels[data.state] || "Estado no disponible"}</strong></span>
          <p className="maintenance-date">{date(data.last_attempt?.at)}</p><p>{labels[data.last_attempt?.reason || ""] || data.last_attempt?.reason}</p>
          <p>No se detiene ni se fuerza una importación para liberar espacio.</p>
        </article>
        <article className="maintenance-card"><h3>Última limpieza completada</h3><strong className="maintenance-figure">{gib(last?.bytes)}</strong>
          <p>retirados · {count(last?.files)} archivos · {last?.seconds == null ? "—" : `${last.seconds.toLocaleString("es-ES")} s`}</p><p className="maintenance-date">{date(last?.at)}</p>
          {last?.files === 0 && <p>No había temporales elegibles. Es un resultado correcto.</p>}
          {data.last_nonempty_cleanup && <p>Última retirada con archivos: {gib(data.last_nonempty_cleanup.bytes)} · {date(data.last_nonempty_cleanup.at)}</p>}
        </article>
      </div>
      <section className="card-block" aria-labelledby="maintenance-space-title">
        <div><h2 id="maintenance-space-title">Espacio disponible por sistema de archivos</h2>
          <p className="card-note">Si temporales e imágenes comparten disco, se muestran juntos una sola vez. Los bytes retirados son tamaño de archivos, no una garantía del incremento de espacio libre.</p></div>
        <div className="maintenance-volumes">{data.volumes.map(volume => <div className="maintenance-volume" key={volume.id}>
          <div className="image-progress-label"><h3>{volume.locations.join(" + ")}</h3><strong>{gib(volume.available_bytes)} libres</strong></div>
          <progress aria-label={`Espacio disponible: ${volume.locations.join(" y ")}`} max={volume.total_bytes} value={volume.available_bytes} />
          <p className="card-note">Capacidad: {gib(volume.total_bytes)}</p>
        </div>)}</div>
        {data.unavailable_locations.length > 0 && <p role="status">Espacio no disponible: {data.unavailable_locations.join(", ")}</p>}
      </section>
      <div className="pair-grid">
        <section className="card-block" aria-labelledby="maintenance-kept-title">
          <h2 id="maintenance-kept-title">Qué se ha conservado</h2>
          <p className="card-note">Se excluyen siempre el último ciclo, el configurado, los activos o pausados y cualquier archivo sin evidencia verificable.</p>
          {last?.excluded.length ? <ul className="maintenance-kept">{last.excluded.map(item => <li key={item.reason}>{item.cycles} {item.cycles === 1 ? "ciclo" : "ciclos"}: {labels[item.reason] || item.reason}.</li>)}</ul> : <p className="card-note">Sin exclusiones registradas en la última limpieza.</p>}
        </section>
        <section className="card-block" aria-labelledby="maintenance-history-title">
          <h2 id="maintenance-history-title">Actividad reciente</h2>
          <ul className="maintenance-history">{data.history.map(event => <li key={event.id}>
            <strong>{eventLabel(event.action)}</strong><time dateTime={event.at}>{date(event.at)}</time>
            {event.files != null && <span>{count(event.files)} archivos · {gib(event.bytes)}</span>}
            {event.reason && <span>{labels[event.reason] || event.reason}</span>}
          </li>)}</ul>
          {!data.history.length && <p className="card-note">Todavía no hay actividad registrada.</p>}
        </section>
      </div>
      <small>Última lectura: {date(data.generated_at)} · Horas del Portal: Canarias. Esta pantalla consulta datos; no inicia borrados.</small>
    </>}
  </section>;
}
